/**
 * סינון לפי תגיות שיחה (SessionConversationTag).
 *
 * טוקני הסינון ברשימת השיחות נשארים בפורמט ההיסטורי — 'noLabel' או
 * JSON.stringify(ChatUserLabel) — כדי לא לשבור userConfig שמור (chatUsersLabelFilter)
 * ותצוגות מותאמות קיימות. ההמרה לשדה conversationTag של מנוע הסינון בנוד
 * (getContacts/searchContacts) מתבצעת רק בשכבת בניית הבקשה.
 */

export type ConversationTagApiCondition = {
  field: 'conversationTag';
  operator: 'contains' | 'isnull';
  value?: string;
};

/**
 * שם התגית מטוקן סינון: 'noLabel' → null (אין תגיות);
 * JSON של ChatUserLabel → name; מחרוזת רגילה → עצמה.
 */
export function tagNameFromLabelFilterToken(token: string): string | null {
  if (!token || token === 'noLabel') return null;
  try {
    const parsed = JSON.parse(token) as { name?: unknown } | null;
    if (parsed && typeof parsed === 'object' && typeof parsed.name === 'string') {
      return parsed.name;
    }
  } catch {
    /* טוקן שהוא שם תגית גולמי */
  }
  return token;
}

/**
 * תנאי API בודד עבור טוקן סינון — contains על שם התגית, או isnull עבור «ללא תגית».
 * בנוד conversationTag הוא אגרגט STRING_AGG של כל התגיות ('tag1||tag2'), ולכן
 * eq משווה את המחרוזת המלאה (נכשל בשיחה עם יותר מתגית אחת); הסמנטיקה של
 * "יש תגית X" היא contains (LIKE על האגרגט).
 */
export function buildConversationTagCondition(token: string): ConversationTagApiCondition {
  const name = tagNameFromLabelFilterToken(token);
  if (name === null) {
    return { field: 'conversationTag', operator: 'isnull' };
  }
  return { field: 'conversationTag', operator: 'contains', value: name };
}

/**
 * התאמה בצד לקוח (הכנסת שורות מאירועי socket) — OR בין טוקנים:
 * 'noLabel' תואם שיחה בלי תגיות; אחרת שם התגית חייב להופיע ב-tags.
 */
export function doTagsMatchLabelFilterTokens(
  tags: string[] | null | undefined,
  tokens: string[],
): boolean {
  if (!tokens || tokens.length === 0) return true;
  const cleanTags = (tags ?? []).filter(
    (t): t is string => typeof t === 'string' && t.trim().length > 0,
  );
  return tokens.some((token) => {
    const name = tagNameFromLabelFilterToken(token);
    if (name === null) return cleanTags.length === 0;
    return cleanTags.includes(name);
  });
}
